/**
 * 订阅到期的判定与文案（纯函数，无 Vue 依赖）。
 *
 * 独立成模块的原因与 `quota-display.ts` 相同：本项目的 vitest 只覆盖 `src/**\/*.test.ts`，
 * 组件不测，所以**凡是需要被测试钉住的规则都必须住在 .ts 文件里**。
 *
 * 徽章（右上角 + 额度条内）、额度页的到期行、Header 的到期横幅三处展示共用这里的
 * 判定，避免"三处各算一次、边界各差一点"。
 *
 * 阈值口径（用户裁决 2026-10-05）：剩余 **≤ 72 小时** 进入即将到期警告。
 * 用小时而不是"自然日"，是因为前者不受时区与日历边界影响，边界用例可以被精确钉住；
 * "剩余 3 个自然日"在跨时区、跨月、夏令时下每次都要重新论证。
 */

import {
  DEFAULT_PLAN_ID,
  planLabelOf,
  UNLIMITED_PLAN_ID,
  type BillingCycle,
  type PlanId,
  type Subscription,
} from '@spirit/contracts';

import { formatDateTime } from './format';

/** 进入"即将到期"警告的剩余时长：精确三天。 */
export const EXPIRY_WARNING_HOURS = 72;
export const EXPIRY_WARNING_MS = EXPIRY_WARNING_HOURS * 60 * 60 * 1000;

/**
 * Header 重新计算剩余时长的周期。
 *
 * 用 60 秒而不是 1 秒：文案粒度是"天/小时"，秒级刷新只会让 Header 每秒重渲染一次，
 * 换不来任何用户能感知的精度。跨到下一个整小时/整天的边界最坏晚 60 秒，无影响。
 */
export const EXPIRY_TICK_MS = 60_000;

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

export type ExpiryKind = 'none' | 'active' | 'expiring' | 'expired';

export interface ExpiryStatus {
  readonly kind: ExpiryKind;
  readonly expiresAt: string | null;
  /** 已过期时截到 0（**不为负**）：要判断"过期了多久"，请用 `kind` 而不是这个数。 */
  readonly remainingMs: number | null;
  /** 向下取整的剩余天数：剩余 1.9 天读作 1 天，宁可保守也不要让用户以为还有两天。 */
  readonly daysLeft: number | null;
  readonly hoursLeft: number | null;
  readonly minutesLeft: number | null;
}

/** 没有订阅 / 拿不到到期时间：结构与"还没到期"不同，调用方要能一眼区分。 */
const NO_EXPIRY: ExpiryStatus = Object.freeze({
  kind: 'none',
  expiresAt: null,
  remainingMs: null,
  daysLeft: null,
  hoursLeft: null,
  minutesLeft: null,
});

/**
 * 订阅的到期状态。
 *
 * 三处刻意的取舍：
 * - **未购买过**（`subscription === null`）返回 `none` 而不是"已过期"：`free` 档用户
 *   没有订阅，不该看到任何到期警告。
 * - **解析不出来的 `expiresAt`** 也按 `none` 处理。契约的 `IsoDateTimeSchema` 会拦住
 *   脏数据，但展示层不该因为一条脏数据把整页炸掉；宁可少提醒一次，也不要凭猜出来的
 *   数字吓用户。
 * - 边界取**闭区间**：剩余恰好 72 小时算"即将到期"。用户说的是"前三天"，
 *   72:00:00 正好是三天整，把它排除在外会让"明天到期"的那档文案在整点闪一下。
 */
export function expiryStatusOf(
  subscription: Subscription | null,
  now: number = Date.now(),
): ExpiryStatus {
  if (subscription === null) {
    return NO_EXPIRY;
  }

  const expiresAtMs = Date.parse(subscription.expiresAt);
  if (!Number.isFinite(expiresAtMs)) {
    return NO_EXPIRY;
  }

  const remainingMs = expiresAtMs - now;
  if (remainingMs <= 0) {
    return {
      kind: 'expired',
      expiresAt: subscription.expiresAt,
      remainingMs: 0,
      daysLeft: 0,
      hoursLeft: 0,
      minutesLeft: 0,
    };
  }

  return {
    kind: remainingMs <= EXPIRY_WARNING_MS ? 'expiring' : 'active',
    expiresAt: subscription.expiresAt,
    remainingMs,
    daysLeft: Math.floor(remainingMs / DAY_MS),
    hoursLeft: Math.floor(remainingMs / HOUR_MS),
    minutesLeft: Math.floor(remainingMs / MINUTE_MS),
  };
}

/** 「将于 2 天后到期」/「今天到期（剩余 5 小时）」/「已到期」。 */
export function describeExpiry(status: ExpiryStatus): string {
  if (status.kind === 'none') {
    return '—';
  }
  if (status.kind === 'expired') {
    return '已到期';
  }
  return describeRemainingAsPhrase(status);
}

/** 「剩余 30 天」/「剩余 5 小时」/「已到期」。额度页的到期行用它。 */
export function describeRemaining(status: ExpiryStatus): string {
  if (status.kind === 'none') {
    return '—';
  }
  if (status.kind === 'expired') {
    return '已到期';
  }
  if (status.daysLeft !== null && status.daysLeft >= 1) {
    return `剩余 ${status.daysLeft} 天`;
  }
  if (status.hoursLeft !== null && status.hoursLeft >= 1) {
    return `剩余 ${status.hoursLeft} 小时`;
  }
  return `剩余 ${status.minutesLeft ?? 0} 分钟`;
}

function describeRemainingAsPhrase(status: ExpiryStatus): string {
  if (status.daysLeft !== null && status.daysLeft >= 1) {
    return `将于 ${status.daysLeft} 天后到期`;
  }
  if (status.hoursLeft !== null && status.hoursLeft >= 1) {
    return `今天到期（剩余 ${status.hoursLeft} 小时）`;
  }
  if (status.minutesLeft !== null && status.minutesLeft >= 1) {
    return `今天到期（剩余 ${status.minutesLeft} 分钟）`;
  }
  return '今天到期（不足 1 分钟）';
}

/**
 * Header 到期横幅的文案；**不该显示横幅时返回 `null`**。
 *
 * 文案用的是**订阅自己的档位**而不是当前生效档位：过期的瞬间服务端已经把
 * `quota.planId` 判成 `free` 了，如果横幅也照着当前的读，用户只会看到
 * "Free 订阅已到期" —— 那既不是事实（Free 没有订阅），也丢掉了"我刚失去的是 Lite"。
 */
export function describeExpiryNotice(
  subscription: Subscription | null,
  status: ExpiryStatus,
): string | null {
  if (subscription === null) {
    return null;
  }

  const label = planLabelOf(subscription.planId);
  const fallback = planLabelOf(DEFAULT_PLAN_ID);

  if (status.kind === 'expired') {
    return `${label} 订阅已到期，额度已回到 ${fallback}`;
  }
  if (status.kind === 'expiring') {
    return `${label} ${describeRemainingAsPhrase(status)}（${formatDateTime(subscription.expiresAt)}），到期后额度自动回到 ${fallback}`;
  }
  return null;
}

/** 「月付」/「年付」。额度页的套餐行要显示它，避免"到底按哪个周期扣的"没处可查。 */
export function describeBillingCycle(cycle: BillingCycle): string {
  return cycle === 'yearly' ? '年付' : '月付';
}

export type PlanBadgeTone = 'free' | 'paid' | 'unlimited' | 'warning';

/**
 * 徽章配色。
 *
 * 到期警示**优先于**档位配色：一个还剩 5 小时的 Lite 徽章如果还是平静的品牌绿，
 * "醒目地提示即将到期"这件事就只完成了一半。
 */
export function planBadgeToneOf(planId: PlanId, status: ExpiryStatus): PlanBadgeTone {
  if (status.kind === 'expiring' || status.kind === 'expired') {
    return 'warning';
  }
  if (planId === UNLIMITED_PLAN_ID) {
    return 'unlimited';
  }
  if (planId === DEFAULT_PLAN_ID) {
    return 'free';
  }
  return 'paid';
}
